import { Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { faqPageSchema, webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';
import { SectionHeading } from '../../../shared/section-heading/section-heading';

const PAGE_PATH = '/how-verification-works';
const PAGE_TITLE = 'How We Verify Coupons | SavingArena';
const PAGE_DESCRIPTION =
  'How SavingArena verifies every coupon: a six-step process of sourcing, manual review, automated testing, user feedback, regular updates and security checks.';

interface VerifyStep {
  title: string;
  description: string;
}

interface VerifyFaq {
  question: string;
  answer: string;
}

@Component({
  selector: 'app-how-verification-works-page',
  imports: [Breadcrumb, RouterLink, BigSaleBanner, DevFlag, SectionHeading],
  templateUrl: './how-verification-works-page.html',
  styleUrl: './how-verification-works-page.scss',
})
export class HowVerificationWorksPage {
  protected readonly crumbs = breadcrumbTrail({ label: 'How Verification Works' });

  private readonly seo = inject(SeoService);

  protected readonly steps: VerifyStep[] = [
    {
      title: 'Source Validation',
      description:
        'We partner with trusted brands and retailers to source authentic coupons directly.',
    },
    {
      title: 'Manual Review',
      description:
        'Our team manually reviews each coupon to ensure it meets our quality standards.',
    },
    {
      title: 'Automated Testing',
      description: 'We use advanced tools to test coupon codes for validity and usability.',
    },
    {
      title: 'User Feedback',
      description: 'We monitor user feedback to identify and remove any non-working coupons.',
    },
    {
      title: 'Regular Updates',
      description: 'Our database is updated regularly to ensure all coupons are current and valid.',
    },
    {
      title: 'Security Checks',
      description: 'We perform security checks to ensure coupons are safe and free from fraud.',
    },
  ];

  protected readonly faqs: VerifyFaq[] = [
    {
      question: 'How do you ensure coupons are valid?',
      answer:
        'We use a combination of manual reviews, automated testing, and user feedback to ensure all coupons are valid and up-to-date.',
    },
    {
      question: "What should I do if a coupon doesn't work?",
      answer:
        "If a coupon doesn't work, please report it to us immediately. Our team will investigate and remove it if necessary.",
    },
    {
      question: 'How often are coupons updated?',
      answer: 'Our database is updated daily to ensure all coupons are current and valid.',
    },
    {
      question: 'Can I use multiple coupons at once?',
      answer:
        "It depends on the retailer's policy. Some stores allow combining coupons, while others do not. Check the terms and conditions for details.",
    },
  ];

  protected readonly expandedFaq = signal<number | null>(0);

  constructor() {
    // Fully static page — the constructor is the right place; the tags are in the DOM before the
    // server serialises the response.
    const url = this.seo.absolute(PAGE_PATH);

    this.seo.apply({
      title: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      canonicalPath: PAGE_PATH,
    });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: PAGE_TITLE,
        description: PAGE_DESCRIPTION,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
      // These FAQs are rendered on the page (the accordion below), so FAQPage schema is honest here.
      faqPageSchema(this.faqs),
    ]);
  }

  protected toggleFaq(index: number): void {
    this.expandedFaq.update((current) => (current === index ? null : index));
  }
}
